import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/firebase';
import RestrictionModal from '@/components/RestrictionModal';
import useLearningRestrictions from '@/hooks/useLearningRestrictions';

interface QuizHistoryRecord {
    id: string;
    shikakuId: string;
    shikakuName?: string;
    playedAt: Date;
    totalScore: number;
    totalQuestions: number;
    percentage: number;
    details: any[];
}

export default function QuizHistoryPage() {
    const navigate = useNavigate();
    const [histories, setHistories] = useState<QuizHistoryRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedHistory, setSelectedHistory] = useState<QuizHistoryRecord | null>(null);
    const { settings } = useLearningRestrictions();
    const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

    useEffect(() => {
        if (!settings.quizEnabled) {
            setIsRestrictionOpen(true);
        }
    }, [settings.quizEnabled]);

    useEffect(() => {
        const fetchHistories = async () => {
            const user = auth.currentUser;
            if (!user) {
                navigate('/login');
                return;
            }

            setLoading(true);
            try {
                const getHistoryFunc = httpsCallable(functions, 'getQuizHistory');

                const result = await getHistoryFunc();
                const data = result.data as any;

                if (data.success && data.histories) {
                    const normalized = data.histories.map((h: any) => {
                        let date: Date | null = null;

                        console.log('[DEBUG] Processing history item:', h.id, 'playedAt:', h.playedAt, 'Type:', typeof h.playedAt);

                        if (h.playedAt) {
                            if (typeof h.playedAt === 'string') {
                                date = new Date(h.playedAt);
                            } else if (typeof h.playedAt === 'number') {
                                date = new Date(h.playedAt);
                            } else if (h.playedAt.seconds) {
                                date = new Date(h.playedAt.seconds * 1000);
                            } else if (h.playedAt._seconds) {
                                date = new Date(h.playedAt._seconds * 1000);
                            } else if (h.playedAt instanceof Date) {
                                date = h.playedAt;
                            } else {
                                // Try generic constructor for other objects? or log warn
                                console.warn('[DEBUG] Unknown date format:', h.playedAt);
                                try {
                                    date = new Date(h.playedAt);
                                } catch (e) {
                                    console.error('[DEBUG] Failed to parse date:', e);
                                }
                            }
                        }

                        // Fallback to Epoch if parsing failed, so it's obvious it's wrong (1970) instead of "Now"
                        if (!date || isNaN(date.getTime())) {
                            console.warn('[DEBUG] Invalid date detected for id:', h.id, 'Using fallback.');
                            date = new Date(0); // 1970-01-01
                        }

                        return {
                            ...h,
                            playedAt: date
                        };
                    });
                    setHistories(normalized);
                } else {
                    setHistories([]);
                }
            } catch (error) {
                console.error('Error fetching quiz history:', error);
                alert('履歴の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchHistories();
    }, [navigate]);

    return (
        <div className="min-h-screen bg-[#F5F5F5]">
            <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
                <div className="max-w-md mx-auto px-4 h-14 flex items-center">
                    <button
                        onClick={() => selectedHistory ? setSelectedHistory(null) : navigate('/quiz/menu')}
                        className="w-10 h-10 flex items-center justify-center -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <i className="ri-arrow-left-s-line text-2xl"></i>
                    </button>
                    <h1 className="flex-1 text-center font-bold text-gray-800 pr-8 text-base">
                        {selectedHistory ? 'クイズ結果詳細' : 'クイズ履歴'}
                    </h1>
                </div>
            </header>

            <main className="max-w-md mx-auto px-4 py-6">
                {loading ? (
                    <div className="text-center py-8 text-gray-500">読み込み中...</div>
                ) : histories.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                        <p className="text-gray-500 mb-4">まだクイズを実施していません</p>
                        <button
                            onClick={() =>
                                settings.quizEnabled
                                    ? navigate('/quiz/play')
                                    : setIsRestrictionOpen(true)
                            }
                            className="px-6 py-2 bg-[#7C3AED] text-white rounded-xl font-bold hover:bg-[#6D28D9] transition shadow-sm"
                        >
                            クイズを始める
                        </button>
                    </div>
                ) : selectedHistory ? (
                    /* Detail View */
                    <div className="bg-white rounded-2xl shadow-sm p-6 animate-fade-in">
                        <div className="text-center mb-6 border-b border-gray-100 pb-6">
                            <h2 className="text-lg font-bold text-gray-800 mb-2">
                                {selectedHistory.shikakuName}
                            </h2>
                            <div className="flex justify-center items-end gap-2 mb-2">
                                <span className="text-4xl font-bold text-[#7C3AED]">
                                    {selectedHistory.totalScore}
                                </span>
                                <span className="text-gray-400 text-lg mb-1">/ {selectedHistory.totalQuestions}</span>
                            </div>
                            <div className="text-sm text-gray-500">
                                正解率: {selectedHistory.percentage}%
                                <span className="mx-2">|</span>
                                {selectedHistory.playedAt.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>

                        <div className="space-y-6">
                            {selectedHistory.details.map((detail: any, index: number) => (
                                <div
                                    key={index}
                                    className={`p-4 rounded-xl ${detail.isCorrect ? 'bg-blue-50 border border-blue-100' : 'bg-red-50 border border-red-100'}`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`text-xl font-bold ${detail.isCorrect ? 'text-blue-500' : 'text-red-500'}`}>
                                            {detail.isCorrect ? 'Correct!' : 'Wrong...'}
                                        </span>
                                        <span className="text-xs font-bold text-gray-400 bg-white px-2 py-1 rounded-full border border-gray-100">
                                            Q{index + 1}
                                        </span>
                                    </div>
                                    <p className="font-bold text-gray-800 mb-3 text-sm">{detail.question}</p>

                                    <div className="bg-white/60 rounded-lg p-3 text-sm space-y-1 mb-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">あなたの回答</span>
                                            <span className={`font-bold ${detail.userAnswer === 1 ? 'text-blue-600' : 'text-red-600'}`}>
                                                {detail.userAnswer === 1 ? '◯' : '✗'}
                                            </span>
                                        </div>
                                        {!detail.isCorrect && (
                                            <div className="flex justify-between border-t border-gray-100 pt-1">
                                                <span className="text-gray-500">正解</span>
                                                <span className="font-bold text-green-600">
                                                    {detail.correctAnswer === 1 ? '◯' : '✗'}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="text-xs text-gray-600 bg-white p-3 rounded-lg border border-gray-100">
                                        <div className="font-bold mb-1 text-gray-400">解説</div>
                                        {detail.explanation}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    /* List View */
                    <div className="space-y-4">
                        {histories.map(history => (
                            <button
                                key={history.id}
                                onClick={() => setSelectedHistory(history)}
                                className="w-full bg-white rounded-2xl shadow-sm p-5 text-left hover:shadow-md transition flex justify-between items-center group"
                            >
                                <div>
                                    <h3 className="font-bold text-gray-800 group-hover:text-[#7C3AED] transition-colors">
                                        {history.shikakuName || '不明な資格'}
                                    </h3>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {history.playedAt.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                <div className="text-right flex flex-col items-end">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-2xl font-bold text-[#7C3AED]">
                                            {history.totalScore}
                                        </span>
                                        <span className="text-sm text-gray-400">/ {history.totalQuestions}</span>
                                    </div>
                                    <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                        {history.percentage}%
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </main>

            <RestrictionModal
                open={isRestrictionOpen}
                message={settings.message}
                actionLabel="学習メニューへ戻る"
                onAction={() => navigate('/learning')}
            />
        </div>
    );
}
